import { type SidebarNavSpaceItem, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  addPinMutation,
  getPersonalSpaceOptions,
  type ListPinsResponse,
  listPinsOptions,
  listPinsQueryKey,
  listSpacesOptions,
  removePinMutation,
  reorderPinsMutation,
  type SpaceSummary,
} from "@/api";
import { reorderPinsLocally } from "./sidebarFolders";
import { hueOf, spaceIcon } from "./spaceIcons";

type SpaceLike = Pick<SpaceSummary, "uuid" | "name" | "icon" | "color" | "isPersonal" | "chatCount">;

function toItem(space: SpaceLike, pinned: boolean): SidebarNavSpaceItem {
  return {
    key: space.uuid,
    name: space.name,
    icon: spaceIcon(space.isPersonal ? "lock" : space.icon),
    hue: hueOf(space.color),
    chatCount: space.chatCount ?? 0,
    pinned,
    private: space.isPersonal,
  };
}

/** Most recent first; a space without a time (an older backend) goes last. */
const byRecentActivity = (a: SpaceSummary, b: SpaceSummary) =>
  (b.lastActivityAt ?? "").localeCompare(a.lastActivityAt ?? "");

/**
 * The sidebar's spaces: the caller's private space, their pinned spaces (in pin order), then every other
 * space they're in, most recently active first (the sidebar shows the first few of those under "All spaces").
 */
export function useSidebarSpaces() {
  const { t } = useTranslation("shell", { keyPrefix: "sidebar.folders" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const pins = useQuery({ ...listPinsOptions(), select: (r) => r.data });
  const personal = useQuery({ ...getPersonalSpaceOptions(), select: (r) => r.data });
  const all = useQuery({ ...listSpacesOptions({ query: { page_size: 100 } }), select: (r) => r.data });
  const refreshPins = () => queryClient.invalidateQueries({ queryKey: listPinsQueryKey() });
  const pin = useMutation({ ...addPinMutation(), onSettled: refreshPins });
  const unpin = useMutation({ ...removePinMutation(), onSettled: refreshPins });
  const reorder = useMutation({
    ...reorderPinsMutation(),
    onError: () => toast.error(t("favoritesSaveFailed")),
    onSettled: refreshPins,
  });

  const spaces = useMemo<SidebarNavSpaceItem[]>(() => {
    const items: SidebarNavSpaceItem[] = [];
    if (personal.data) items.push(toItem(personal.data, false));
    const pinned = new Set<string>();
    for (const space of pins.data ?? []) {
      if (space.isPersonal) continue;
      pinned.add(space.uuid);
      items.push(toItem(space, true));
    }
    for (const space of [...(all.data ?? [])].sort(byRecentActivity)) {
      if (!space.isPersonal && !pinned.has(space.uuid)) items.push(toItem(space, false));
    }
    return items;
  }, [pins.data, personal.data, all.data]);

  return {
    spaces,
    togglePin: (uuid: string) => {
      if (spaces.some((s) => s.key === uuid && s.pinned)) unpin.mutate({ path: { space_uuid: uuid } });
      else pin.mutate({ body: { spaceUuid: uuid } });
    },
    reorderPins: (order: string[]) => {
      // Shown at once; a refetch in flight would overwrite it, so it's cancelled.
      void queryClient.cancelQueries({ queryKey: listPinsQueryKey() });
      queryClient.setQueryData<ListPinsResponse>(listPinsQueryKey(), (old) =>
        old ? { ...old, data: reorderPinsLocally(old.data, order) } : old,
      );
      reorder.mutate({ body: { spaceUuids: order } });
    },
  };
}

/** Whether the sidebar lists this space (the private space, or one the caller is in). Undefined while loading. */
export function useSpaceInSidebar(spaceUuid: string): boolean | undefined {
  const personal = useQuery({ ...getPersonalSpaceOptions(), select: (r) => r.data });
  const all = useQuery({ ...listSpacesOptions({ query: { page_size: 100 } }), select: (r) => r.data });
  if (!personal.data || !all.data) return undefined;
  return personal.data.uuid === spaceUuid || all.data.some((space) => space.uuid === spaceUuid);
}
